import { useEffect, useRef } from "react";
import { thumbUrl } from "@/lib/images";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { propertyUrl, tr } from "@/lib/translations";
import { createMap } from "@/lib/leafletMap";

const icon = L.divIcon({
  className: "",
  html: '<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:rgb(var(--c-brand));border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);transform:rotate(-45deg)"></div>',
  iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -24],
});

// Zähler-Marker für eng beieinanderliegende Objekte (klappt beim Zoomen/Klicken auf)
const clusterIcon = (cluster) => L.divIcon({
  className: "",
  html: `<div style="width:40px;height:40px;border-radius:50%;background:rgb(var(--c-brand));border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);color:#fff;font:600 14px 'Open Sans',sans-serif;display:flex;align-items:center;justify-content:center">${cluster.getChildCount()}</div>`,
  iconSize: [40, 40], iconAnchor: [20, 20],
});

export const PropertiesMap = ({ properties, lang }) => {
  const el = useRef(null);
  const nav = useNavigate();
  const withGeo = properties.filter((p) => p.lat != null && p.lng != null);
  // Desktop (Maus): Details beim Überfahren des Markers; Touch-Geräte: Marker antippen
  const hoverable = typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  useEffect(() => {
    const node = el.current;
    if (!node) return;
    const map = createMap(node, { scrollWheelZoom: false, center: [28.3, -15.8], zoom: 7 });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png",
      { attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' }).addTo(map);
    const group = L.markerClusterGroup({ iconCreateFunction: clusterIcon, maxClusterRadius: 45, showCoverageOnHover: false, spiderfyOnMaxZoom: true, zoomToBoundsOnClick: true, spiderLegPolylineOptions: { weight: 1.5, color: "#2d6b0d", opacity: 0.6 } });
    withGeo.forEach((p) => {
      const title = tr(p.title, lang);
      const html = `<div style="width:190px;font-family:'Open Sans',sans-serif">
          <img src="${thumbUrl(p.hero_image)}" style="width:100%;height:100px;object-fit:cover;border-radius:6px" alt=""/>
          <div style="font-size:11px;color:rgb(var(--c-signal));font-weight:600;margin-top:6px">Nr. ${p.ref_number ?? ""}</div>
          <div style="font-weight:600;font-size:14px;color:#333">${title}</div>
          <div style="font-size:12px;color:#666">${p.location} · ab €${p.price_per_night}/Nacht</div>
          <a href="${propertyUrl(p)}" data-url="${propertyUrl(p)}" style="display:inline-block;margin-top:8px;background:rgb(var(--c-brand));color:#fff;padding:6px 10px;border-radius:6px;font-size:12px;text-decoration:none">Zum Objekt</a>
        </div>`;
      const marker = L.marker([p.lat, p.lng], { icon }).bindPopup(html, { autoPan: !hoverable, closeButton: !hoverable }).addTo(group);
      marker.on("add", () => marker.getElement()?.setAttribute("aria-label", title));  // kein title-Attribut → kein Browser-Tooltip neben dem Kärtchen
      if (hoverable) {
        let timer;
        const close = () => { timer = setTimeout(() => marker.closePopup(), 250); };
        const keep = () => clearTimeout(timer);
        // Kärtchen ohne Kartenverschiebung so platzieren, dass es im Kartenausschnitt bleibt (unten statt oben, seitlich eingerückt)
        const open = () => {
          keep(); marker.openPopup();
          const popup = marker.getPopup(), pe = popup.getElement(), size = map.getSize(), pt = map.latLngToContainerPoint(marker.getLatLng());
          const w = pe.offsetWidth, h = pe.offsetHeight;
          const dx = Math.min(Math.max(pt.x, w / 2 + 8), size.x - w / 2 - 8) - pt.x;
          const below = pt.y - 24 - h < 8;
          popup.options.offset = L.point(dx, below ? h + 34 : 0);
          pe.classList.toggle("ph-pop-notip", below || dx !== 0);
          popup.update();
        };
        marker.on("mouseover", open);
        marker.on("mouseout", close);
        marker.on("popupopen", (e) => { const pe = e.popup.getElement(); pe.addEventListener("mouseenter", keep); pe.addEventListener("mouseleave", close); });
      }
    });
    group.addTo(map);
    if (withGeo.length) map.fitBounds(group.getBounds().pad(0.25), { animate: false });
    const onClick = (e) => {
      const a = e.target.closest("a[data-url]");
      if (a) { e.preventDefault(); nav(a.dataset.url); }
    };
    node.addEventListener("click", onClick);
    return () => { node.removeEventListener("click", onClick); map.safeRemove(); };
  }, [withGeo.map((p) => p.id).join(","), lang, hoverable]);

  return (
    <div className="rounded-2xl overflow-hidden border border-line bg-white" data-testid="properties-map">
      <div ref={el} style={{ height: 460, width: "100%" }} />
      <div className="px-4 py-2 text-xs text-stone-500 flex justify-between">
        <span data-testid="properties-map-hint">{withGeo.length} Objekte auf der Karte · {hoverable ? "Maus über einen Marker bewegen für Details" : "Marker antippen für Details"} · Zahl = mehrere Objekte, anklicken zum Aufklappen</span>
        <span>OpenStreetMap</span>
      </div>
    </div>
  );
};
